import { useEffect } from "react";
import { useT } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import { Printer, X, Phone, Mail, MapPinned } from "lucide-react";
import { format, parseISO, differenceInCalendarDays } from "date-fns";
import { ptBR, enUS, es } from "date-fns/locale";
import MovixLogo from "@/components/MovixLogo";
import { COMPANY } from "@/lib/company";

const DATE_LOCALES = { pt: ptBR, en: enUS, es };

export default function Receipt({ open, onClose, rental, type }) {
  const { t, lang, fmtCurrency } = useT();
  const dateLocale = DATE_LOCALES[lang] || enUS;

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    if (open) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !rental) return null;

  const fmtDate = (iso) => iso ? format(parseISO(iso), "dd/MM/yyyy", { locale: dateLocale }) : "—";

  const rate = rental.billing_period === "semanal" ? rental.weekly_rate : rental.daily_rate;
  const rateLabel = rental.billing_period === "semanal" ? t("rentals.weekly") : t("rentals.daily");
  const rentalTotal = Number(rental.total || 0);
  const deposit = Number(rental.deposit || 0);
  const charges = rental.extra_charges || rental.tolls || [];
  const chargesTotal = charges.reduce((a, c) => a + Number(c.amount || 0), 0);
  const pickupTotal = rentalTotal + deposit;
  const refund = deposit - chargesTotal;

  // Calculate quantity (days or weeks) — for open-ended rentals, default to 1 cycle
  const days = rental.return_date
    ? Math.max(1, differenceInCalendarDays(parseISO(rental.return_date), parseISO(rental.pickup_date)))
    : (rental.billing_period === "semanal" ? 7 : (rental.billing_period === "mensal" ? 30 : 1));
  const weeks = Math.max(1, Math.ceil(days / 7));
  const qty = rental.billing_period === "semanal" ? weeks : days;
  const qtyUnit = rental.billing_period === "semanal"
    ? (qty === 1 ? t("rentals.week") : t("rentals.weeks"))
    : (qty === 1 ? t("rentals.day") : t("rentals.days"));
  const billingDescription = `${qty} ${qtyUnit} × ${fmtCurrency(rate)}/${rental.billing_period === "semanal" ? t("rentals.perWeek").replace("/", "") : t("rentals.perDay").replace("/", "")}`;

  const isPickup = type === "pickup";
  const handlePrint = () => window.print();

  return (
    <>
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .print-area, .print-area * { visibility: visible; }
          .print-area { position: absolute; left: 0; top: 0; width: 100%; }
          .no-print { display: none !important; }
        }
      `}</style>

      <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center overflow-y-auto p-4 no-print" onClick={onClose}>
        <div className="bg-white max-w-2xl w-full my-8 relative" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between px-6 py-3 border-b border-zinc-200 bg-zinc-50 no-print">
            <div className="overline">{isPickup ? t("receipt.pickup") : t("receipt.return")}</div>
            <div className="flex items-center gap-2">
              <Button onClick={handlePrint} className="rounded-none bg-[#F5A623] hover:bg-[#D97706]" data-testid="print-receipt-button">
                <Printer className="w-4 h-4 mr-2" /> {t("common.print")}
              </Button>
              <button onClick={onClose} className="p-1.5 border border-zinc-200 hover:border-zinc-400"><X className="w-4 h-4" /></button>
            </div>
          </div>

          <div className="print-area p-10 font-mono text-sm" data-testid={`receipt-${type}`}>
            {/* Branded header */}
            <div className="bg-black text-white -mx-10 -mt-10 px-10 py-6 mb-6">
              <div className="flex items-start justify-between gap-6">
                <MovixLogo variant="light" showTagline size="md" />
                <div className="text-right text-[10px] text-white/70 leading-relaxed font-mono">
                  <div className="flex items-center justify-end gap-2"><Phone className="w-3 h-3 text-[#F5A623]" /> {COMPANY.phone}</div>
                  <div className="flex items-center justify-end gap-2"><Mail className="w-3 h-3 text-[#F5A623]" /> {COMPANY.email}</div>
                  <div className="flex items-center justify-end gap-2"><MapPinned className="w-3 h-3 text-[#F5A623]" /> {COMPANY.address}</div>
                </div>
              </div>
            </div>

            <div className="border-b-2 border-black pb-4 mb-6 flex items-start justify-between">
              <div>
                <div className="text-xs text-zinc-600">{t("receipt.receiptNo")} {rental.id?.slice(0, 8).toUpperCase()}</div>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-[0.2em] font-bold">
                  {isPickup ? t("receipt.pickupTitle") : t("receipt.returnTitle")}
                </div>
                <div className="text-xs text-zinc-600 mt-1">{t("receipt.issuedAt")} {format(new Date(), "dd/MM/yyyy HH:mm", { locale: dateLocale })}</div>
              </div>
            </div>

            <Section title={t("receipt.customer")}>
              <Row k={t("customers.fullName")} v={rental.customer?.name} />
              <Row k={t("customers.ssn")} v={rental.customer?.social_security} />
              <Row k={t("customers.license")} v={rental.customer?.driver_license} />
              <Row k={t("customers.phone")} v={rental.customer?.phone} />
            </Section>

            <Section title={t("receipt.vehicle")}>
              <Row k={t("fleet.model")} v={rental.car?.model} />
              <Row k={t("fleet.plate")} v={rental.car?.plate} />
              <Row k={t("fleet.color")} v={rental.car?.color} />
              <Row k={t("fleet.year")} v={rental.car?.year} />
            </Section>

            <Section title={t("receipt.period")}>
              <Row k={t("receipt.pickupDate")} v={fmtDate(rental.pickup_date)} />
              <Row k={t("receipt.returnDate")} v={fmtDate(rental.return_date)} />
              <Row k={t("receipt.quantity")} v={`${qty} ${qtyUnit}`} />
              <Row k={t("receipt.unitRate")} v={`${rateLabel} · ${fmtCurrency(rate)}`} />
              <Row k={t("receipt.billing")} v={billingDescription} />
              <Row k={t("receipt.rentalValue")} v={fmtCurrency(rentalTotal)} />
            </Section>

            {isPickup ? (
              <Section title={t("receipt.pickupTotalSection")}>
                <Line k={t("receipt.rental")} v={fmtCurrency(rentalTotal)} />
                <Line k={t("receipt.depositRefundable")} v={fmtCurrency(deposit)} />
                <div className="border-t-2 border-black mt-3 pt-3 flex items-center justify-between">
                  <div className="font-display font-bold tracking-tight">{t("receipt.totalPaid")}</div>
                  <div className="font-display font-black text-2xl tracking-tighter">{fmtCurrency(pickupTotal)}</div>
                </div>
                <p className="text-xs text-zinc-600 mt-4 leading-relaxed">
                  {t("receipt.pickupNote", { deposit: fmtCurrency(deposit) })}
                </p>
              </Section>
            ) : (
              <>
                <Section title={t("receipt.tollsSection")}>
                  <Line k={t("receipt.depositPaid")} v={fmtCurrency(deposit)} />
                  {charges.length > 0 ? (
                    <div className="mt-3 border border-zinc-300">
                      <table className="w-full text-xs">
                        <thead className="bg-zinc-100 border-b border-zinc-300">
                          <tr className="text-left">
                            <th className="px-3 py-2 font-semibold">{t("common.date")}</th>
                            <th className="px-3 py-2 font-semibold">{t("rentalDetails.category")}</th>
                            <th className="px-3 py-2 font-semibold">{t("receipt.ezpassCol")}</th>
                            <th className="px-3 py-2 font-semibold text-right">{t("common.value")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {charges.map((c) => (
                            <tr key={c.id} className="border-b border-zinc-200">
                              <td className="px-3 py-2">{fmtDate(c.date)}</td>
                              <td className="px-3 py-2 uppercase text-[10px] tracking-wider">{t(`rentalDetails.categories.${c.category || "ezpass"}`)}</td>
                              <td className="px-3 py-2">{c.description}</td>
                              <td className="px-3 py-2 text-right">{fmtCurrency(c.amount)}</td>
                            </tr>
                          ))}
                          <tr className="bg-zinc-50">
                            <td className="px-3 py-2 font-semibold" colSpan={3}>{t("receipt.tollsTotal")}</td>
                            <td className="px-3 py-2 text-right font-semibold">{fmtCurrency(chargesTotal)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-xs text-zinc-500 mt-3">{t("receipt.noTolls")}</div>
                  )}
                </Section>

                <Section title={t("receipt.refundSection")}>
                  <Line k={t("receipt.depositLine")} v={fmtCurrency(deposit)} />
                  <Line k={t("receipt.tollsDeduct")} v={`− ${fmtCurrency(chargesTotal)}`} />
                  <div className="border-t-2 border-black mt-3 pt-3 flex items-center justify-between">
                    <div className="font-display font-bold tracking-tight">{t("receipt.refundedAmount")}</div>
                    <div className={`font-display font-black text-2xl tracking-tighter ${refund < 0 ? "text-red-700" : ""}`}>
                      {fmtCurrency(refund)}
                    </div>
                  </div>
                  {refund < 0 && (
                    <p className="text-xs text-red-700 mt-3">
                      {t("receipt.excessNote", { excess: fmtCurrency(Math.abs(refund)) })}
                    </p>
                  )}
                </Section>
              </>
            )}

            <div className="mt-12 grid grid-cols-2 gap-12">
              <div>
                <div className="border-t border-black pt-2 text-xs text-center">{t("receipt.signCustomer")}</div>
                <div className="text-xs text-zinc-500 text-center mt-1">{rental.customer?.name}</div>
              </div>
              <div>
                <div className="border-t border-black pt-2 text-xs text-center">{t("receipt.signCompany")}</div>
                <div className="text-xs text-zinc-500 text-center mt-1">{t("receipt.responsible")}</div>
              </div>
            </div>

            <div className="mt-8 pt-4 border-t border-zinc-200 text-center text-[10px] text-zinc-400 uppercase tracking-[0.2em]">
              {t("receipt.eDoc")} · {COMPANY.fullName} · {format(new Date(), "yyyy-MM-dd")}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-6">
      <div className="overline mb-2 text-zinc-700">{title}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}
function Row({ k, v }) {
  return (
    <div className="flex justify-between text-xs">
      <span className="text-zinc-500">{k}</span>
      <span className="font-medium">{v || "—"}</span>
    </div>
  );
}
function Line({ k, v }) {
  return (
    <div className="flex justify-between text-sm py-1">
      <span>{k}</span>
      <span className="font-semibold">{v}</span>
    </div>
  );
}
